import React, { ChangeEvent, FormEvent, useState } from "react";
import { confirmSignIn } from "aws-amplify/auth";
import { clearError, showError } from "../helpers/formHelper";

interface NewPasswordFormElements extends HTMLFormControlsCollection {
  password: HTMLInputElement;
  confirmPassword: HTMLInputElement;
}

interface NewPasswordFormElement extends HTMLFormElement {
  readonly elements: NewPasswordFormElements;
}

export function NewPasswordForm() {
  const [error, setError] = useState("");
  const [isValid, setIsValid] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
  });

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    const password = event.target.value;

    setIsValid({
      length: password.length >= 8,
      lowercase: !!password.match(/[a-z]/),
      uppercase: !!password.match(/[A-Z]/),
      number: !!password.match(/[0-9]/),
      special: !!password.match(/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/),
    });
  }

  async function onSubmit(event: FormEvent<NewPasswordFormElement>) {
    event.preventDefault();

    const form = event.currentTarget;
    const { password, confirmPassword } = form.elements;

    clearError(setError, form);

    if (!form.checkValidity()) {
      return showError("Please enter a password.", setError, form);
    }

    if (Object.values(isValid).some(valid => !valid)) {
      return showError(
        "Password must satisfy the above requirements.",
        setError,
        form);
    }

    if (password.value !== confirmPassword.value) {
      return showError(
        "Passwords must match. Please try again.",
        setError,
        form,
        confirmPassword);
    }

    try {
      await confirmSignIn({ challengeResponse: form.elements.password.value });
    }
    catch {
      showError("Failed to set password. Please try again.", setError, form);
    }
  }

  function getValidityClass(isValid: boolean) {
    return isValid ? "is-valid" : "is-invalid";
  }

  return (
    <form noValidate onSubmit={onSubmit} className="d-flex flex-column gap-2 w-100 mt-4">
      <span>Password must</span>
      <ul>
        <li className={getValidityClass(isValid.length)}>be at least 8 characters long</li>
        <li className={getValidityClass(isValid.lowercase)}>have at least 1 lowercase letter</li>
        <li className={getValidityClass(isValid.uppercase)}>have at least 1 uppercase letter</li>
        <li className={getValidityClass(isValid.number)}>have at least 1 number</li>
        <li className={getValidityClass(isValid.special)}>have at least 1 special character</li>
      </ul>

      <label htmlFor="password">Password</label>
      <input
        className="form-control"
        type="password"
        id="password"
        name="password"
        required
        onChange={onChange}
      />

      <label htmlFor="confirmPassword">Confirm password</label>
      <input
        className="form-control"
        type="password"
        id="confirmPassword"
        name="confirmPassword"
        required
      />

      <button className="btn btn-success">Update password</button>
      <div className="invalid-feedback">{error}</div>
    </form>
  );
}
